import type { IAnalyticsProperties } from "../product-analytics/product-analytics.types";
import type { IUserMemoryStarterPrompt } from "../types/user-memory";
import { EUserMemoryStarterPromptIcon } from "../types/user-memory";

// NOTE: NSAI-6559 — how many rows the empty chat shows, personalized or not, so
// the block keeps its height either way.
export const STARTER_PROMPTS_COUNT = 4;

// NOTE: NSAI-5822 / NSAI-6259 — the static starter prompts, grounded in what the
// product does: a meeting recap, the day's schedule, unread mail and a Drive
// import. They are what a new user sees, what anyone sees while memory is off,
// and what fills the rows a personalized list leaves empty. The keys double as
// the rows' test-id suffixes (`chat-starter-prompt-schedule`), so they are part
// of the web and mobile E2E contract — do not rename them.
//
// NOTE: The literals are kept typed (`as const`) because the i18n key type is
// generated from the locale files, so `t()` only accepts them as literals.
export const DEFAULT_STARTER_PROMPTS = [
  {
    key: "meeting_recap",
    labelKey: "input.starters.meeting_recap",
    icon: EUserMemoryStarterPromptIcon.MEETING,
  },
  {
    key: "schedule",
    labelKey: "input.starters.schedule",
    icon: EUserMemoryStarterPromptIcon.CALENDAR,
  },
  {
    key: "emails",
    labelKey: "input.starters.emails",
    icon: EUserMemoryStarterPromptIcon.EMAIL,
  },
  {
    key: "drive_import",
    labelKey: "input.starters.drive_import",
    icon: EUserMemoryStarterPromptIcon.IMPORT,
  },
] as const;

export type TDefaultStarterPromptLabelKey =
  (typeof DEFAULT_STARTER_PROMPTS)[number]["labelKey"];

// NOTE: One rendered row. `key` is stable per row — the default's own key, or
// `personalized-<n>` — and is what both apps use as the React key and the
// test-id suffix. `isPersonalized` lets a row say where it came from (the web
// tooltip, the mobile accessibility hint) without the UI re-deriving it.
export interface IStarterPromptRow {
  key: string;
  text: string;
  icon: EUserMemoryStarterPromptIcon;
  isPersonalized: boolean;
}

export interface IBuildStarterPromptRowsProps {
  personalized: readonly IUserMemoryStarterPrompt[] | undefined;
  translateDefault: (labelKey: TDefaultStarterPromptLabelKey) => string;
}

const toKnownIcon = (icon: string): EUserMemoryStarterPromptIcon =>
  Object.values(EUserMemoryStarterPromptIcon).find((known) => known === icon) ??
  EUserMemoryStarterPromptIcon.IDEA;

// NOTE: NSAI-6559 — the list the empty chat renders. Personalized suggestions
// come first; the static defaults fill whatever they leave, in their usual
// order, so the block is always STARTER_PROMPTS_COUNT rows long:
//
// - no personalized list (new user, memory off, request failed or still
//   loading) → the four defaults, exactly as before;
// - a partial list → the personalized rows, then defaults;
// - a row whose text repeats an earlier one (case-insensitive) is skipped, so a
//   generated "What's on my schedule today?" does not sit above the identical
//   default;
// - an icon this client does not know (a newer server) draws as IDEA rather
//   than as nothing.
export const buildStarterPromptRows = ({
  personalized,
  translateDefault,
}: IBuildStarterPromptRowsProps): IStarterPromptRow[] => {
  const candidates: IStarterPromptRow[] = [
    ...(personalized ?? []).map((prompt, index) => ({
      key: `personalized-${index}`,
      text: prompt.text.trim(),
      icon: toKnownIcon(prompt.icon),
      isPersonalized: true,
    })),
    ...DEFAULT_STARTER_PROMPTS.map(({ key, labelKey, icon }) => ({
      key,
      text: translateDefault(labelKey).trim(),
      icon,
      isPersonalized: false,
    })),
  ];

  const seenTexts = new Set<string>();
  const rows: IStarterPromptRow[] = [];

  for (const candidate of candidates) {
    const normalizedText = candidate.text.toLocaleLowerCase();
    if (normalizedText.length === 0 || seenTexts.has(normalizedText)) {
      continue;
    }
    seenTexts.add(normalizedText);
    rows.push(candidate);
    if (rows.length === STARTER_PROMPTS_COUNT) {
      break;
    }
  }

  return rows;
};

export interface IBuildStarterPromptClickedPropertiesProps {
  row: IStarterPromptRow;
  index: number;
}

// NOTE: NSAI-6559 — the properties of `chat_starter_prompt_clicked`, built in
// one place so web and mobile report the same shape. Whether the row came from
// memory and where it sat is what the question "do personalized suggestions get
// used?" needs; the text itself never leaves the device — a personalized row
// paraphrases the user's memory.
export const buildStarterPromptClickedProperties = ({
  row,
  index,
}: IBuildStarterPromptClickedPropertiesProps): IAnalyticsProperties => ({
  is_personalized: row.isPersonalized,
  position: index,
});
